import { ResourceWrapper } from "../ResourceWrapper.ts";
import Semantic from "../../../eventer/Semantic.ts";
import { LocalEventerPod } from "../local-eventer-pod.ts";

import { FetchPodEventsRequest } from "@eventer/api-client";
import { ApiResponseResult } from "../../../appfw/api-client/ApiClient.ts";
import { LoadPodEventsData } from "../../shared/eventer-pod.ts";

export class EventsFeed {

    readonly localEventerPod: LocalEventerPod;
    readonly sessionId: null | string;
    readonly remotePod: string;

    readonly startDate: Date

    // current min/max of startDate of all loaded events
    // Null means: all events have been loaded, no more events available in that direction
    startDateMin: Date | null
    startDateMax: Date | null

    events: undefined | ResourceWrapper[] = undefined

    prevUrl: undefined | string | null = undefined

    private loadNextPromise: Promise<ResourceWrapper[]> | undefined = undefined
    nextUrl: undefined | string | null = undefined

    constructor(
        localEventerPod: LocalEventerPod,
        sessionId: null | string,
        remotePod: string,
    ) {
        this.localEventerPod = localEventerPod
        this.sessionId = sessionId
        this.remotePod = remotePod
        const now = new Date()
        this.startDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            0, 0, 0, 0
        )
        this.startDateMin = this.startDate
        this.startDateMax = this.startDate
    }

    hasPrev(): boolean {
        return this.prevUrl !== null
    }

    async loadPrev(): Promise<ResourceWrapper[]> {
        throw Error("Not implemented")
    }

    hasNext(): boolean {
        return this.nextUrl !== null
    }

    loadNext(): Promise<ResourceWrapper[]> {

        if (this.loadNextPromise) {
            return this.loadNextPromise
        }

        if (this.nextUrl === null) {
            return Promise.resolve([])
        }

        console.log("EventsFeed.loadNext()", this.remotePod, this.nextUrl)

        let result: Promise<ApiResponseResult<LoadPodEventsData<ResourceWrapper>>>
        if (this.nextUrl === undefined) {
            const request: FetchPodEventsRequest = {
                pod: this.remotePod,
                startDateMin: this.startDate,
                limit: 5,
                sort: 'startDate_asc',
                expand: ["image"],
            }
            result = this.localEventerPod.loadPodEvents({
                request: request,
                sessionId: this.sessionId || undefined
            })
        } else {
            result = this.localEventerPod.loadNextPodEvents({
                url: this.nextUrl,
                sessionId: this.sessionId || undefined
            })
        }

        this.loadNextPromise = result
            .then(result => {

                if (result.data) {

                    this.putEvents(result.data.events)

                    console.log("set nextUrl =", result.data.nextUrl)
                    if (result.data.nextUrl === undefined) {
                        // no more events
                        this.nextUrl = null
                        this.startDateMax = null
                    } else {
                        this.nextUrl = result.data.nextUrl
                    }

                    return result.data.events
                }

                // no data?
                if (result.response.status > 400) {
                    console.warn("loadNext failed with status", result.response.status)
                }

                // feed exhausted, mark as "finished"
                this.nextUrl = null
                this.startDateMax = null

                return []
            })
            .finally(() => this.loadNextPromise = undefined)

        return this.loadNextPromise
    }

    private putEvents(newEvents: ResourceWrapper[]): boolean {

        let changed = false;

        if (this.events === undefined) {
            this.events = newEvents
            changed = true
        } else {
            const map = new Map<string, ResourceWrapper>();
            for (const event of this.events) {
                map.set(event.subject, event)
            }
            for (const event of newEvents) {
                const existing = map.get(event.subject)
                if (!existing || !existing.equals(event)) {
                    map.set(event.subject, event)
                    changed = true;
                }
            }
            if (changed) {
                this.events = Array.from(map.values()).sort((a, b) => {
                    const aTime = a.getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri)?.getTime() ?? 0;
                    const bTime = b.getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri)?.getTime() ?? 0;
                    return aTime - bTime;
                })
            }
        }

        if (changed) {
            const startDates = this.events!
                .map(event => event.getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri))
                .filter((date): date is Date => date !== undefined)
            if (startDates.length > 0) {
                this.startDateMin = new Date(Math.min(this.startDate.getTime(), ...startDates.map(date => date.getTime())))
                this.startDateMax = new Date(Math.max(this.startDate.getTime(), ...startDates.map(date => date.getTime())))
            }
        }
        return changed
    }
}

const eventFeedsCache = new Map<string, EventsFeed>()

export function getEventsFeed(
    localEventerPod: LocalEventerPod,
    sessionId: null | string,
    remotePod: string,
): EventsFeed {
    const key = `${localEventerPod.name}-${sessionId || 'null'}-${remotePod}`
    let entry = eventFeedsCache.get(key)
    if (entry === undefined) {
        console.log("Add new EventsFeed entry", key)
        entry = new EventsFeed(localEventerPod, sessionId, remotePod)
        eventFeedsCache.set(key, entry)
    }
    return entry
}
